import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { ArrowLeft, CheckCircle, Send, XCircle, Archive, ExternalLink } from 'lucide-react'
import {
  useContentItem,
  useContentActions,
  useUpdateContentItem,
  useTransitionContentItem,
  CONTENT_TYPE_LABELS,
} from '../hooks/useContent'
import { ContentStatusBadge, ConfidencePill } from '../components/ContentStatusBadge'
import { RoomEditor } from '../components/editors/RoomEditor'
import { ArtworkEditor } from '../components/editors/ArtworkEditor'
import { ArtistEditor } from '../components/editors/ArtistEditor'
import { EaselEditorialEditor } from '../components/editors/EaselEditorialEditor'
import { MockupEditor } from '../components/editors/MockupEditor'
import { GallerySetEditor } from '../components/editors/GallerySetEditor'
import { GalleryWallSetEditor } from '../components/editors/GalleryWallSetEditor'
import { ShoppableRoomEditor } from '../components/editors/ShoppableRoomEditor'
import { BlogEditorialEditor } from '../components/editors/BlogEditorialEditor'
import { RecommendationEditor } from '../components/editors/RecommendationEditor'
import type { ContentItem } from '../api/contentApi'
import { formatDateTime } from '@/lib/utils'

export function ContentItemPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [editorDirty, setEditorDirty] = useState(false)
  const { data: item, isLoading } = useContentItem(id || null)
  const { data: actions } = useContentActions(id || null)
  const update = useUpdateContentItem()
  const transition = useTransitionContentItem()

  if (isLoading || !item) {
    return (
      <div className="container mx-auto py-6 space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  const academy = item.contentType === 'blog_editorial' && item.payload.publicationDestination === 'academy'
  const onSave = (payload: Record<string, unknown>) => update.mutate({ id: item.id, data: { payload } })
  const act = (action: 'approve' | 'review' | 'reject' | 'archive') => transition.mutate({ id: item.id, action })
  const violations = (item.violations as unknown[]) || []
  // ContentHandler.approve records the publish adapter's return value on the action
  // row (after.publish). For blog_editorial that carries the live WordPress URL, so
  // read it back from the audit trail rather than adding a field to the item.
  const publishedUrl = (() => {
    for (const a of actions || []) {
      const after = a.after as { publish?: { wpUrl?: string; academyUrl?: string } } | null
      if (after?.publish?.academyUrl) return after.publish.academyUrl
      if (after?.publish?.wpUrl) return after.publish.wpUrl
    }
    return null
  })()

  return (
    <div className="container mx-auto py-6 space-y-6 max-w-5xl">
      <Button variant="ghost" size="sm" onClick={() => navigate('/app/content')}>
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to queue
      </Button>

      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl font-semibold">{item.title}</h1>
            <ContentStatusBadge status={item.status} />
            <ConfidencePill value={item.confidence} />
          </div>
          <p className="text-muted-foreground text-sm mt-1">
            {CONTENT_TYPE_LABELS[item.contentType] || item.contentType} · {item.targetRef}
            {item.targetUrl && (
              <a href={item.targetUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 ml-2 text-primary">
                <ExternalLink className="h-3 w-3" />
                view
              </a>
            )}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap justify-end">
          {item.status !== 'review' && (
            <Button variant="outline" size="sm" onClick={() => act('review')}>
              <Send className="h-4 w-4 mr-1" />
              {academy && item.status === 'active' ? 'Unpublish & review' : 'Send to review'}
            </Button>
          )}
          {/* Already live: the action is a REPUBLISH, and saying "Approve & publish" on a
              published article reads as though it has not gone out yet. The adapter looks
              the post up by slug and updates it, so this never creates a duplicate — but
              the label has to say what it does. */}
          <Button
            size="sm"
            variant={item.status === 'active' ? 'outline' : 'default'}
            onClick={() => act('approve')}
            disabled={transition.isPending || update.isPending || (academy && editorDirty)}
          >
            <CheckCircle className="h-4 w-4 mr-1" />
            {academy && editorDirty ? 'Save changes first' : item.status === 'active' ? 'Republish' : 'Approve & publish'}
          </Button>
          <Button variant="outline" size="sm" onClick={() => act('archive')}>
            <Archive className="h-4 w-4 mr-1" />
            {academy && item.status === 'active' ? 'Unpublish & archive' : 'Archive'}
          </Button>
          <Button variant="destructive" size="sm" onClick={() => act('reject')}>
            <XCircle className="h-4 w-4 mr-1" />
            Reject
          </Button>
        </div>
      </div>

      {publishedUrl && (!academy || item.status === 'active') && (
        <Card className="border-emerald-200 bg-emerald-50/50 dark:bg-emerald-950/10 dark:border-emerald-900">
          <CardContent className="py-3 flex items-center justify-between gap-4">
            <p className="text-sm text-emerald-900 dark:text-emerald-300">{academy ? 'Published to Academy.' : 'Published to the Journal.'}</p>
            <a
              href={publishedUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-sm font-medium text-emerald-900 dark:text-emerald-300 hover:underline"
            >
              View live article
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </CardContent>
        </Card>
      )}

      {violations.length > 0 && (
        <Card className="border-amber-200 bg-amber-50/50 dark:bg-amber-950/10 dark:border-amber-900">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Quality-gate findings</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="text-sm text-amber-800 dark:text-amber-300 list-disc pl-5">
              {violations.map((v, i) => (
                <li key={i}>{typeof v === 'string' ? v : JSON.stringify(v)}</li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Content</CardTitle>
          <CardDescription>
            {item.contentType === 'blog_editorial'
              ? academy ? 'Edit the Academy story. Save changes before approving or republishing. Sending it to review, rejecting it or archiving it removes the published version from Academy.' : 'Edit the article. Approving publishes it to the Journal at blog.myarthaus.com.'
              : 'Edit the curator-controlled fields. Approving publishes them to Shopify.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {item.contentType === 'blog_editorial' ? (
            <BlogEditorialEditor item={item} onSave={onSave} saving={update.isPending} onDirtyChange={setEditorDirty} />
          ) : item.contentType === 'room_collection' ? (
            <RoomEditor item={item} onSave={onSave} saving={update.isPending} />
          ) : item.contentType === 'artwork_editorial' ? (
            <ArtworkEditor item={item} onSave={onSave} saving={update.isPending} />
          ) : item.contentType === 'artist_editorial' ? (
            <ArtistEditor item={item} onSave={onSave} saving={update.isPending} />
          ) : item.contentType === 'easel_artwork_editorial' ? (
            <EaselEditorialEditor item={item} onSave={onSave} saving={update.isPending} />
          ) : item.contentType === 'mockup' ? (
            <MockupEditor item={item} onSave={onSave} saving={update.isPending} />
          ) : item.contentType === 'gallery_set' ? (
            <GallerySetEditor item={item} onSave={onSave} saving={update.isPending} />
          ) : item.contentType === 'gallery_wall_set' ? (
            <GalleryWallSetEditor item={item} onSave={onSave} saving={update.isPending} />
          ) : item.contentType === 'shoppable_room' ? (
            <ShoppableRoomEditor item={item} onSave={onSave} saving={update.isPending} />
          ) : item.contentType === 'recommendations' ? (
            <RecommendationEditor item={item} onSave={onSave} saving={update.isPending} />
          ) : (
            <GenericPayloadEditor item={item} onSave={onSave} saving={update.isPending} />
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Activity</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {(actions || []).map((a) => (
              <div key={a.id} className="flex items-start gap-3 text-sm">
                <span className="text-xs text-muted-foreground w-36 shrink-0">{formatDateTime(a.createdAt)}</span>
                <span className="capitalize font-medium">{a.actorName || a.actor}</span>
                <span className="text-muted-foreground">
                  {a.action}
                  {a.note ? ` — ${a.note}` : ''}
                </span>
              </div>
            ))}
            {(!actions || actions.length === 0) && <p className="text-sm text-muted-foreground">No activity yet.</p>}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

function GenericPayloadEditor({ item, onSave, saving }: { item: ContentItem; onSave: (p: Record<string, unknown>) => void; saving?: boolean }) {
  const [text, setText] = useState(JSON.stringify(item.payload || {}, null, 2))
  const [err, setErr] = useState<string | null>(null)
  const save = () => {
    try {
      const parsed = JSON.parse(text)
      setErr(null)
      onSave(parsed)
    } catch (e) {
      setErr((e as Error).message)
    }
  }
  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">No specialized editor for this content type yet — edit the payload JSON directly.</p>
      <Textarea rows={16} value={text} onChange={(e) => setText(e.target.value)} className="font-mono text-xs" />
      {err && <p className="text-sm text-red-600">{err}</p>}
      <div className="flex justify-end">
        <Button onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</Button>
      </div>
    </div>
  )
}
